import {
  type DynamicClient,
  InvalidParamError,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import type { TransferAmountParams } from '@dynamic-labs-sdk/client/core';
import {
  assertDefined,
  getWalletProviderFromWalletAccount,
  parseAmountToSmallestUnit,
} from '@dynamic-labs-sdk/client/core';
import {
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
  getAssociatedTokenAddress,
} from '@solana/spl-token';
import type { TransactionInstruction } from '@solana/web3.js';
import {
  PublicKey,
  SystemProgram,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';

import { NotSolanaProviderError } from '../../errors/NotSolanaProviderError';
import { getSolanaConnection } from '../../getSolanaConnection';
import { isSolanaGasSponsorshipEnabled } from '../../isSolanaGasSponsorshipEnabled';
import { isSolanaWalletAccount } from '../../isSolanaWalletAccount';
import { isSolanaWalletProvider } from '../../isSolanaWalletProvider';
const DEFAULT_DECIMALS = 9;

/**
 * Transfers native SOL or SPL tokens from one wallet to another.
 *
 * For native SOL, builds a `SystemProgram.transfer` instruction.
 * For SPL tokens, derives associated token accounts, creates the recipient
 * ATA if needed, and builds a `transferChecked` instruction. Supports both
 * standard SPL tokens and Token-2022 tokens by detecting the mint's program.
 */
export const solanaTransferAmount = async (
  { amount, recipient, token, walletAccount }: TransferAmountParams,
  client: DynamicClient
): Promise<{ transactionHash: string }> => {
  if (!isSolanaWalletAccount(walletAccount)) {
    throw new InvalidParamError(
      'Wallet account is not a Solana wallet account'
    );
  }

  const provider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (!isSolanaWalletProvider(provider)) {
    throw new NotSolanaProviderError({ providerKey: provider.key });
  }

  const { networkData } = await getActiveNetworkData({ walletAccount }, client);
  assertDefined(networkData, 'Network data is required');

  const connection = getSolanaConnection({ networkData });

  const fromPubkey = new PublicKey(walletAccount.address);
  const toPubkey = new PublicKey(recipient);

  let instructions: TransactionInstruction[];

  if (token) {
    const decimals = token.decimals ?? DEFAULT_DECIMALS;
    const mintPubkey = new PublicKey(token.address);

    // Detect Token-2022 vs standard SPL by checking mint account owner
    const mintAccountInfo = await connection.getAccountInfo(mintPubkey);
    const tokenProgramId = mintAccountInfo?.owner.equals(TOKEN_2022_PROGRAM_ID)
      ? TOKEN_2022_PROGRAM_ID
      : TOKEN_PROGRAM_ID;

    const senderAta = await getAssociatedTokenAddress(
      mintPubkey,
      fromPubkey,
      false,
      tokenProgramId
    );

    const recipientAta = await getAssociatedTokenAddress(
      mintPubkey,
      toPubkey,
      false,
      tokenProgramId
    );

    const recipientAccountInfo = await connection.getAccountInfo(recipientAta);

    const rawAmount = parseAmountToSmallestUnit({ amount, decimals });

    instructions = [];

    if (!recipientAccountInfo) {
      instructions.push(
        createAssociatedTokenAccountInstruction(
          fromPubkey,
          recipientAta,
          toPubkey,
          mintPubkey,
          tokenProgramId
        )
      );
    }

    instructions.push(
      createTransferCheckedInstruction(
        senderAta,
        mintPubkey,
        recipientAta,
        fromPubkey,
        rawAmount,
        decimals,
        [],
        tokenProgramId
      )
    );
  } else {
    instructions = [
      SystemProgram.transfer({
        fromPubkey,
        lamports: parseAmountToSmallestUnit({ amount, decimals: DEFAULT_DECIMALS }),
        toPubkey,
      }),
    ];
  }

  const { blockhash } = await connection.getLatestBlockhash('finalized');

  const message = new TransactionMessage({
    instructions,
    payerKey: fromPubkey,
    recentBlockhash: blockhash,
  }).compileToV0Message();

  const transaction = new VersionedTransaction(message);

  if (
    isSolanaGasSponsorshipEnabled(client) &&
    provider.signAndSendSponsoredTransaction
  ) {
    const { signature } = await provider.signAndSendSponsoredTransaction({
      transaction,
      walletAccount,
    });
    return { transactionHash: signature };
  }

  const { signature } = await provider.signAndSendTransaction({
    transaction,
    walletAccount,
  });

  return { transactionHash: signature };
};
